import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ExternalPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // Track if ACH or card
  
  const sessionId = searchParams.get('session_id');
  const slug = searchParams.get('slug');

  // Process payment on mount (like internal payment flow)
  useEffect(() => {
    const processPayment = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setProcessing(false);
        return;
      }

      // CRITICAL: Must call backend to send email and post transactions
      // Use RELATIVE URL so it always calls the correct backend for the environment
      try {
        console.log('✅ External payment success - calling backend to process:', sessionId);
        
        // Use relative URL - will call production backend when on production, preview when on preview
        const response = await axios.get(`/api/external-payments/success?session_id=${sessionId}`);
        
        console.log('Backend response:', response.data);
        setPaymentStatus(response.data.payment_status || 'processing');
        setProcessing(false);
      } catch (err) {
        // Backend call failed BUT payment is still valid
        // Just show success page anyway
        console.log('Backend call failed but showing success:', err);
        setPaymentStatus('processing');
        setProcessing(false);
      }
    };

    processPayment();
  }, [sessionId]);

  useEffect(() => {
    if (!processing && !error) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Use absolute URL to ensure redirect to correct domain
            window.location.href = `https://hoamanagerpro.us/p/${slug}`;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [slug, navigate, processing, error]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
        {processing ? (
          <>
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h1>
            <p className="text-gray-600">
              Please wait while we confirm your payment.
            </p>
          </>
        ) : error ? (
          <>
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Received</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.href = `https://hoamanagerpro.us/p/${slug}`}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Return to HOA Page
            </button>
          </>
        ) : (
          <>
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {paymentStatus === 'processing' ? 'Payment Submitted!' : 'Payment Successful!'}
            </h1>
            <p className="text-gray-600 mb-6">
              {paymentStatus === 'processing' 
                ? 'Your bank payment has been submitted successfully. It will be processed in 2-5 business days.'
                : 'Your payment has been received and processed successfully.'}
            </p>
            
            {paymentStatus === 'processing' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800 font-semibold mb-3">
                  🏦 Bank Transfer (ACH) - Processing
                </p>
                <p className="text-sm text-blue-800 mb-3">
                  Your bank payment has been submitted successfully. Your bank account will be debited in 2-5 business days.
                </p>
                <p className="text-sm text-blue-800">
                  You'll receive a confirmation email once the payment clears. If you have questions, please contact the HOA board.
                </p>
              </div>
            )}
            
            {paymentStatus === 'paid' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800 mb-2">
                  <strong>✓</strong> Receipt sent to your email
                </p>
                <p className="text-sm text-green-800 mb-2">
                  <strong>✓</strong> Transaction recorded in HOA ledger
                </p>
                <p className="text-sm text-green-800">
                  <strong>✓</strong> Budget updated
                </p>
              </div>
            )}
            
            <p className="text-sm text-gray-500 mb-4">
              Transaction ID: <code className="bg-gray-100 px-2 py-1 rounded">{sessionId?.slice(0, 20)}...</code>
            </p>
            
            <button
              onClick={() => window.location.href = `https://hoamanagerpro.us/p/${slug}`}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors mb-2"
            >
              Return to HOA Page
            </button>
            
            <p className="text-xs text-gray-400">
              Redirecting automatically in {countdown} seconds...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalPaymentSuccess;
